import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Input, Space, Tooltip, Typography } from "antd";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";

const { Text } = Typography;

const WIDTH = 115;
const HEIGHT = 24;

export default function InlineEditableCustomerCode({
  value,
  onSave,
  disabled,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current.input;
      el?.focus?.();
      const len = (draft ?? "").length;
      el?.setSelectionRange?.(len, len);
    }
  }, [editing]);

  const handleSave = () => {
    setEditing(false);
    onSave?.(draft.trim());
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft(value ?? "");
  };

  // Blur anywhere outside the compact editor cancels edit mode
  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (!wrapRef.current) return handleCancel();
    if (next && wrapRef.current.contains(next)) return; // focus stayed inside
    handleCancel();
  };

  if (editing && !disabled) {
    return (
      <div
        ref={wrapRef}
        onBlur={handleBlur}
        style={{ display: "inline-block", lineHeight: 0 }}
      >
        <Space.Compact size="small">
          <Input
            ref={inputRef}
            size="small"
            style={{ width: WIDTH, height: HEIGHT, boxShadow: "none" }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onPressEnter={handleSave}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleCancel();
            }}
            placeholder={"Type here..."}
          />
          <Button
            type="default"
            style={{
              marginLeft: 1,
              height: HEIGHT,
              color: "#389e0d",
              background: "#f6ffed",
              borderColor: "#b7eb8f",
            }}
            icon={<CheckOutlined />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSave}
          />
          <Button
            type="default"
            style={{
              height: HEIGHT,
              color: "#cf1322",
              background: "#fff1f0",
              borderColor: "#ffa39e",
            }}
            icon={<CloseOutlined />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCancel}
          />
        </Space.Compact>
      </div>
    );
  }

  return (
    <Tooltip title={disabled ? "" : "Click to edit"}>
      <span
        role={disabled ? "text" : "button"}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) setEditing(true);
        }}
        style={{
          width: WIDTH,
          height: HEIGHT,
          display: "inline-flex",
          alignItems: "center",
          padding: "0 7px",
          border: "1px solid transparent",
          borderRadius: 0, // square corners
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = "#d9d9d9";
          e.currentTarget.style.background = "#f5f5f5";
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.background = "transparent";
        }}
      >
        <Text>{isEmpty(value) ? "None" : value}</Text>
      </span>
    </Tooltip>
  );
}
